/* eslint-disable @typescript-eslint/ban-ts-comment */
/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  DevToolsEnhancerOptions,
  Middleware,
  StoreEnhancer,
  Store,
} from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import { produce } from 'immer';
import { setAuthChangeListener } from '@opengeoweb/authentication';
import type { DataExplorerTypes } from '@opengeoweb/data-explorer';
import {
  dataExplorerMiddlewares,
  dataExplorerReducersMap,
} from '@opengeoweb/data-explorer';
import type { layerSelectTypes } from '@opengeoweb/layer-select';
import {
  layerSelectReducersMap,
  layerSelectMiddlewares,
} from '@opengeoweb/layer-select';
import { sentryReduxEnhancerOptions } from '@opengeoweb/sentry';
import { snackbarMiddlewares, snackbarReducersMap } from '@opengeoweb/snackbar';
import { soundingsReducersMap } from '@opengeoweb/soundings';
import type { SoundingsModuleState } from '@opengeoweb/soundings';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  storeReducerMap,
  storeMiddlewares,
  unifyReducerMaps,
  configureOpenGeoWebStore,
  commonActions,
} from '@opengeoweb/store';
import type { TimeSeriesModuleState } from '@opengeoweb/time-series';
import {
  timeSeriesReducersMap,
  timeSeriesMiddlewares,
} from '@opengeoweb/time-series';
import { warningsReducersMap, warningsMiddlewares } from '@opengeoweb/warnings';
import type { WorkspaceModuleStore } from '@opengeoweb/workspace';
import {
  workspaceReducersMap,
  workspaceMiddlewares,
} from '@opengeoweb/workspace';

export interface AppStore
  extends
    WorkspaceModuleStore,
    CoreAppStore,
    layerSelectTypes.LayerSelectModuleState,
    DataExplorerTypes.DataExplorerModuleState,
    TimeSeriesModuleState,
    SoundingsModuleState {}

const devToolsStateSanitizer: DevToolsEnhancerOptions = {
  // @ts-expect-error
  stateSanitizer: (state: AppStore) => {
    return produce(state, (draftState) => {
      if (state['services']) {
        Object.values(state['services'].byId).forEach((service) => {
          if (draftState?.['services'] && service.id) {
            // @ts-expect-error
            draftState['services'].byId[service.id].layers =
              service.layers?.length;
          }
        });
      }
    });
  },
};

// ---> Sentry related code
const sentryReduxEnhancer = createReduxEnhancer(sentryReduxEnhancerOptions);
// Sentry related code <--

const libraryReducerMaps = [
  storeReducerMap,
  layerSelectReducersMap,
  timeSeriesReducersMap,
  warningsReducersMap,
  workspaceReducersMap,
  snackbarReducersMap,
  dataExplorerReducersMap,
  soundingsReducersMap,
];

const allReducers = unifyReducerMaps(libraryReducerMaps);
export const geowebRootReducer = combineReducers(allReducers);

export const geowebMiddlewares: Middleware[] = Array.from(
  new Set([
    ...storeMiddlewares,
    ...layerSelectMiddlewares,
    ...timeSeriesMiddlewares,
    ...warningsMiddlewares,
    ...workspaceMiddlewares,
    ...snackbarMiddlewares,
    ...dataExplorerMiddlewares,
  ]),
);

export const initAuthSync = (store: Store): void => {
  setAuthChangeListener((isLoggedIn, auth) => {
    if (isLoggedIn && auth) {
      store.dispatch(commonActions.login({ isLoggedIn }));
      store.dispatch(commonActions.setCredentials({ credentials: auth }));
    } else {
      store.dispatch(commonActions.logout());
    }
  });
};

const store = configureOpenGeoWebStore(
  {},
  allReducers,
  geowebMiddlewares,
  [sentryReduxEnhancer] as StoreEnhancer[],
  process.env.NODE_ENV === 'development' ? devToolsStateSanitizer : false,
);

initAuthSync(store);

export { store };
